import React, { useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ajaxFetch } from '~/helpers/api';
import apiCalls from '~/helpers/apiCalls';
import { routes } from '~/helpers/routes';
import { SPA_PREFIX } from '~/dashboard/constants';
import { UserContext } from '~/dashboard/context';
import LoadingView from '~/dashboard/views/LoadingView';
import { RULE_LIBRARY_ORGANIZATION_SLUG } from '~/constants';
import {
  buildRuleLibrarySessionUrl,
  getRuleLibraryCustomerId,
  isNonProdRuleLibraryHost,
  PRIMARY_RULE_LIBRARY_BASE_URL,
  PRIMARY_RULE_LIBRARY_CUSTOMER_ID,
} from '../utils';

export const LibraryRouteGuard = ({ children }: { children: JSX.Element }) => {
  const [status, setStatus] = useState<'pending' | 'allowed' | 'denied'>('pending');

  const { customer, adminCustomerAccess, getUserContext } = useContext(UserContext);
  const organizationNameSlug = customer?.organizationNameSlug;

  const ruleLibraryCustomerId = getRuleLibraryCustomerId(adminCustomerAccess);

  useEffect(() => {
    let cancelled = false;

    const ensureContext = async () => {
      if (!organizationNameSlug) {
        if (!cancelled) setStatus('pending');
        return;
      }

      if (organizationNameSlug === RULE_LIBRARY_ORGANIZATION_SLUG) {
        if (!cancelled) setStatus('allowed');
        return;
      }

      let targetRuleLibraryCustomerId = ruleLibraryCustomerId;
      if (!targetRuleLibraryCustomerId) {
        try {
          const customerData = await ajaxFetch(routes.admin.customers.data, { camelCase: true });
          targetRuleLibraryCustomerId = getRuleLibraryCustomerId(customerData?.customers);
        } catch {
          targetRuleLibraryCustomerId = undefined;
        }
      }

      if (!targetRuleLibraryCustomerId) {
        const shouldRedirectToPrimaryRuleLibraryCell = !isNonProdRuleLibraryHost(
          window.location.hostname,
        );

        if (shouldRedirectToPrimaryRuleLibraryCell) {
          const nextUrl = `${window.location.pathname}${window.location.search || ''}${window.location.hash || ''}`;
          window.location.assign(buildRuleLibrarySessionUrl(
            PRIMARY_RULE_LIBRARY_BASE_URL,
            PRIMARY_RULE_LIBRARY_CUSTOMER_ID,
            nextUrl,
          ));
          return;
        }

        if (!cancelled) setStatus('denied');
        return;
      }

      try {
        await apiCalls.customers.switchCustomerContext(targetRuleLibraryCustomerId);
        await getUserContext?.();
        if (!cancelled) setStatus('allowed');
      } catch {
        if (!cancelled) setStatus('denied');
      }
    };

    ensureContext();
    return () => { cancelled = true; };
  }, [organizationNameSlug, ruleLibraryCustomerId, getUserContext]);

  if (status === 'denied') return <Navigate replace to={`${SPA_PREFIX}/error/401/`} />;
  if (status !== 'allowed') return <LoadingView />;

  return children;
};
